import { venueZone } from "../format.js";
import { hasFeature } from "../features.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  familyOrder,
  isDextAddress,
  isEmailAddress,
  POS_BACKFILL_DAYS,
  POS_ENVIRONMENTS,
  sameTarget,
  type PosEnvironment,
  type PosIntegration,
  type PosItem,
  type PosTarget,
} from "@baros/core";
import { useApp } from "../context.js";
import { OrgApiError, type OrgVenue } from "./api.js";
import { integrationsApi, type DextState, type SquareState } from "./integrations-api.js";
import "./integrations.css";

interface Props {
  readonly venues: readonly OrgVenue[];
}

const ENVIRONMENT_LABEL: Readonly<Record<PosEnvironment, string>> = {
  production: "Production",
  sandbox: "Sandbox",
};

function message(err: unknown): string {
  return err instanceof OrgApiError || err instanceof Error ? err.message : "That did not work";
}

/** An instant as somebody reads it: `02 Oct 2026, 9:15 pm`. */
function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-AU", { timeZone: venueZone(),
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const byName = (a: { readonly name: string }, b: { readonly name: string }) =>
  a.name.localeCompare(b.name);

/**
 * One section's busy / error / note, and the wrapper that sets them.
 *
 * Each card keeps its own rather than borrowing OrgScreen's banner: a refusal
 * from Square belongs beside the token field that caused it, not at the top
 * of a page the person has scrolled away from. `run` resolves to whether the
 * action landed, and never rejects, so a caller can `void` it safely.
 */
function useRun() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const run = useCallback(
    async (key: string, action: () => Promise<string | null>): Promise<boolean> => {
      setBusy(key);
      setError(null);
      setMsg(null);
      try {
        const note = await action();
        if (note) setMsg(note);
        return true;
      } catch (err) {
        setError(message(err));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  return { busy, error, msg, run };
}

/**
 * The Integrations tab — Square for the till, Dext for the bookkeeper. See
 * docs/integrations.md.
 *
 * Unlike the other three tabs this one is not fed by OrgScreen's overview
 * fetch: the two integrations are separate documents behind separate routes,
 * either may be absent, and one failing to load must not blank the other. So
 * each card fetches and refetches for itself and takes only the venue list
 * from above.
 */
export function Integrations({ venues }: Props) {
  const app = useApp();
  return (
    <>
      {hasFeature(app.me, "square") && <Square venues={venues} />}
      {hasFeature(app.me, "dext") && <Dext />}
    </>
  );
}

// -------------------------------------------------------------------- Square

function Square({ venues }: Props) {
  const app = useApp();
  const [state, setState] = useState<SquareState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const { busy, error, msg, run } = useRun();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await integrationsApi.square(app.token);
      setState({ square: res.square ?? null, sync: res.sync ?? {} });
    } catch (err) {
      setLoadError(message(err));
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every mutation returns the connection row; the sync map is the GET's alone. */
  const patch = (square: PosIntegration) =>
    setState((current) => ({ square, sync: current?.sync ?? {} }));

  const square = state?.square ?? null;

  const connect = (accessToken: string, environment: PosEnvironment) =>
    run("connect", async () => {
      const res = await integrationsApi.connectSquare(app.token, accessToken, environment);
      patch(res.square);
      setReplacing(false);
      return square
        ? `Token replaced. Connected to ${res.square.merchantName}.`
        : `Connected to ${res.square.merchantName}. Link each venue to its Square location next.`;
    });

  return (
    <>
      <div className="org-section">
        <h3>Square · point of sale</h3>
        <div className="org-note">
          BarOS reads completed sales from Square every fifteen minutes: takings for the cash-up,
          units for menu engineering, and what each drink took off the shelf.
        </div>

        {loadError && (
          <div className="msg err">
            {loadError}
            <div style={{ marginTop: 10 }}>
              <button className="btn sm" onClick={() => void load()}>
                Try again
              </button>
            </div>
          </div>
        )}
        {!state && !loadError && <div className="label">Reading the Square connection…</div>}
        {error && <div className="msg err">{error}</div>}
        {msg && <div className="msg ok">{msg}</div>}

        {state && !square && (
          <div className="org-card">
            <TokenForm busy={busy === "connect"} submitLabel="Connect Square" onSubmit={connect} />
          </div>
        )}

        {state && square && (
          <div className="org-card">
            <div>
              <div className="kv">
                <span className="k">Merchant</span>
                <span className="v">{square.merchantName || square.merchantId}</span>
              </div>
              <div className="kv">
                <span className="k">Environment</span>
                <span className="v">
                  <span className="chip">
                    {ENVIRONMENT_LABEL[square.environment] ?? square.environment}
                  </span>
                </span>
              </div>
              <div className="kv">
                <span className="k">Status</span>
                <span className={`v ${square.status === "error" ? "neg" : "pos"}`}>
                  {square.status === "error" ? "Error" : "Connected"}
                </span>
              </div>
              <div className="kv">
                <span className="k">Connected</span>
                <span className="v">{when(square.connectedAt)}</span>
              </div>
              <div className="kv">
                <span className="k">Token last checked</span>
                <span className="v">{when(square.checkedAt)}</span>
              </div>
            </div>

            {square.status === "error" && (
              <div className="msg err">
                {square.lastError || "The last call to Square failed."} Replacing the token
                usually clears this.
              </div>
            )}

            {replacing ? (
              <TokenForm
                busy={busy === "connect"}
                submitLabel="Replace the token"
                environment={square.environment}
                onSubmit={connect}
                onCancel={() => setReplacing(false)}
              />
            ) : (
              <div className="row int-actions">
                <button className="btn" disabled={busy !== null} onClick={() => setReplacing(true)}>
                  Replace token
                </button>
                <span className="spacer" />
                <button
                  className="btn danger"
                  disabled={busy !== null}
                  onClick={() =>
                    void run("disconnect", async () => {
                      const ok = window.confirm(
                        `Disconnect ${square.merchantName || "Square"}? The token is deleted and syncing stops. Sales already read are kept.`,
                      );
                      if (!ok) return null;
                      await integrationsApi.disconnectSquare(app.token);
                      setState({ square: null, sync: {} });
                      return "Square disconnected. The sales history is kept.";
                    })
                  }
                >
                  {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {state && square && (
        <>
          <VenueLinks
            venues={venues}
            state={state}
            square={square}
            busy={busy}
            onLink={(venue, locationId) =>
              run(venue.venueId, async () => {
                const res = await integrationsApi.linkVenue(app.token, venue.venueId, locationId);
                patch(res.square);
                return locationId
                  ? `${venue.name} linked. Its first sync reads ${POS_BACKFILL_DAYS} days back.`
                  : `${venue.name} unlinked. Its sales history is kept.`;
              })
            }
          />

          <div className="org-section">
            <h3>Options</h3>
            <div className="org-card">
              <div className="row int-option">
                <div className="org-note">
                  <strong>Take sales off stock.</strong> A sale of a mapped item takes its recipe
                  off the venue's stock levels; unmapped items take nothing.
                </div>
                <OnOff
                  label="Take sales off stock"
                  value={square.decrementStock === true}
                  disabled={busy !== null}
                  onChange={(decrementStock) =>
                    void run("options", async () => {
                      const res = await integrationsApi.setOptions(app.token, decrementStock);
                      patch(res.square);
                      return decrementStock
                        ? "Mapped sales now come off stock."
                        : "Sales no longer move stock.";
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Keyed on the merchant so a different account starts from its own list. */}
          <TillItems key={square.merchantId} onRefreshed={load} />
        </>
      )}
    </>
  );
}

/**
 * The token field, for a first connection and for replacing one.
 *
 * The token lives in this component's state for as long as it takes to send
 * and no longer: cleared on success, never read back from the API, never put
 * in a URL. It is deliberately not a `<form>` — a form with a password field
 * is what makes a browser offer to save it.
 */
function TokenForm(props: {
  readonly busy: boolean;
  readonly submitLabel: string;
  readonly environment?: PosEnvironment;
  readonly onSubmit: (accessToken: string, environment: PosEnvironment) => Promise<boolean>;
  readonly onCancel?: () => void;
}) {
  const [accessToken, setAccessToken] = useState("");
  const [environment, setEnvironment] = useState<PosEnvironment>(
    props.environment ?? "production",
  );

  return (
    <>
      <div className="org-grid2">
        <label className="field">
          <span>Access token</span>
          <input
            type="password"
            // Not "off": browsers ignore that on a password field and offer
            // the saved BarOS password, or to save the token as one.
            autoComplete="new-password"
            autoCapitalize="off"
            spellCheck={false}
            value={accessToken}
            disabled={props.busy}
            onChange={(e) => setAccessToken(e.target.value)}
          />
        </label>
        <div className="field">
          <span>Environment</span>
          <div className="row" role="group" aria-label="Square environment">
            {POS_ENVIRONMENTS.map((env) => (
              <button
                key={env}
                className="btn"
                aria-pressed={environment === env}
                disabled={props.busy}
                onClick={() => setEnvironment(env)}
              >
                {ENVIRONMENT_LABEL[env]}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="org-note">
        The token is in the Square Developer Dashboard › your application › Credentials › Access
        token. BarOS only reads sales with it and never writes to Square; once saved it is not
        shown again, here or anywhere.
      </div>
      <div className="row int-actions">
        <span className="spacer" />
        {props.onCancel && (
          <button className="btn" disabled={props.busy} onClick={props.onCancel}>
            Cancel
          </button>
        )}
        <button
          className="btn primary"
          disabled={props.busy || !accessToken.trim()}
          onClick={async () => {
            const landed = await props.onSubmit(accessToken.trim(), environment);
            // A refused token stays in the field so a typo can be fixed.
            if (landed) setAccessToken("");
          }}
        >
          {props.busy ? "Checking with Square…" : props.submitLabel}
        </button>
      </div>
    </>
  );
}

/** A two-button switch in the house style — `aria-pressed`, no checkbox. */
function OnOff(props: {
  readonly label: string;
  readonly value: boolean;
  readonly disabled?: boolean;
  readonly onChange: (value: boolean) => void;
}) {
  return (
    <div className="row int-onoff" role="group" aria-label={props.label}>
      <button
        className="btn"
        aria-pressed={props.value}
        disabled={props.disabled}
        onClick={() => !props.value && props.onChange(true)}
      >
        On
      </button>
      <button
        className="btn"
        aria-pressed={!props.value}
        disabled={props.disabled}
        onClick={() => props.value && props.onChange(false)}
      >
        Off
      </button>
    </div>
  );
}

/**
 * Which Square location each venue's sales are read from.
 *
 * The select is a draft until Save: linking is what starts a venue's backfill
 * and the moment its sales begin to move stock, so it should not happen
 * because a thumb brushed a dropdown.
 */
function VenueLinks(props: {
  readonly venues: readonly OrgVenue[];
  readonly state: SquareState;
  readonly square: PosIntegration;
  readonly busy: string | null;
  readonly onLink: (venue: OrgVenue, locationId: string | null) => Promise<boolean>;
}) {
  const { square, state } = props;
  const [drafts, setDrafts] = useState<Readonly<Record<string, string>>>({});
  const locations = square.locations ?? [];
  const links = square.venues ?? {};

  return (
    <div className="org-section">
      <h3>Venues</h3>
      {props.venues.length === 0 && (
        <div className="org-note">No venues yet — add one on the Venues tab, then link it here.</div>
      )}
      {props.venues.length > 0 && (
        <div className="table">
          {props.venues.map((v) => {
            const link = links[v.venueId];
            const linked = link?.locationId ?? "";
            const chosen = drafts[v.venueId] ?? linked;
            const sync = state.sync[v.venueId];
            // A location Square no longer lists must still show as the choice.
            const orphan = linked !== "" && !locations.some((l) => l.id === linked);
            return (
              <div className="trow org-trow int-venue" key={v.venueId}>
                <span className="m-name">
                  <span className="n">{v.name}</span>
                  <span className="s">
                    {!link
                      ? "Not linked — no sales are read for this venue"
                      : sync
                        ? `Last sync ${when(sync.lastRunAt)} · ${sync.lastRunSales ?? 0} new ${sync.lastRunSales === 1 ? "sale" : "sales"}`
                        : `Linked ${when(link.linkedAt)} · not synced yet`}
                  </span>
                  {link && sync?.lastError && <span className="s neg">{sync.lastError}</span>}
                </span>
                <span className="int-pick">
                  <select
                    aria-label={`Square location for ${v.name}`}
                    value={chosen}
                    disabled={props.busy !== null}
                    onChange={(e) => setDrafts((d) => ({ ...d, [v.venueId]: e.target.value }))}
                  >
                    <option value="">Not linked</option>
                    {orphan && <option value={linked}>Unknown location ({linked})</option>}
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                        {l.status && l.status !== "ACTIVE" ? ` (${l.status.toLowerCase()})` : ""}
                      </option>
                    ))}
                  </select>
                </span>
                <span className="m-act">
                  <button
                    className="btn sm"
                    disabled={props.busy !== null || chosen === linked}
                    onClick={async () => {
                      const landed = await props.onLink(v, chosen || null);
                      if (!landed) return;
                      setDrafts((d) =>
                        Object.fromEntries(Object.entries(d).filter(([k]) => k !== v.venueId)),
                      );
                    }}
                  >
                    {props.busy === v.venueId ? "Saving…" : "Save"}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- till items

/** What a row's controls hold. Text for the measure — a person is typing it. */
interface Draft {
  readonly kind: "" | PosTarget["kind"];
  readonly specId: string;
  readonly ingredientId: string;
  readonly ml: string;
}

function draftOf(target: PosTarget | null): Draft {
  return {
    kind: target?.kind ?? "",
    specId: target?.kind === "spec" ? target.specId : "",
    ingredientId: target?.kind === "ingredient" ? target.ingredientId : "",
    // A standard pour, so the commonest straight-spirit mapping is one select.
    ml: target?.kind === "ingredient" ? String(target.ml) : "30",
  };
}

/** The target a draft describes: null is "not mapped", undefined is "not finished". */
function targetOf(draft: Draft): PosTarget | null | undefined {
  switch (draft.kind) {
    case "":
      return null;
    case "ignore":
      return { kind: "ignore" };
    case "spec":
      return draft.specId ? { kind: "spec", specId: draft.specId } : undefined;
    case "ingredient": {
      const ml = Number(draft.ml);
      return draft.ingredientId && Number.isFinite(ml) && ml > 0
        ? { kind: "ingredient", ingredientId: draft.ingredientId, ml }
        : undefined;
    }
  }
}

/** Unmapped first — the list to work through — then guesses, then the settled. */
const rank = (item: PosItem) => (!item.target ? 0 : item.auto ? 1 : 2);

interface Named {
  readonly id: string;
  readonly name: string;
}

/**
 * The mapping list: every item the till has shown, and what BarOS takes it
 * to be.
 *
 * A row's edit stays in the row until its own Save. Each save recomputes four
 * weeks of nights at every linked venue, so it is one deliberate request per
 * item, never one per keystroke — and a half-chosen ingredient pour never
 * reaches the API at all.
 */
function TillItems(props: { readonly onRefreshed: () => Promise<void> }) {
  const app = useApp();
  const [items, setItems] = useState<readonly PosItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { busy, error, msg, run } = useRun();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await integrationsApi.items(app.token);
      setItems(res.items ?? []);
    } catch (err) {
      setLoadError(message(err));
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  // Retired specs are not offered: the till is selling something, and it is
  // not that. Dilution is in a build for the ABV sum and is nothing anybody
  // pours, so it is not offered as a pour either. Recipes are listed as the
  // library lists them, each primary with its variants indented under it, so
  // choosing between a Negroni and a Rum Negroni is choosing within one group.
  const specs = useMemo<readonly Named[]>(
    () =>
      familyOrder(app.library.specs.filter((s) => s.status !== "retired")).map((s, i, all) => ({
        id: s.specId,
        name:
          s.variantOf && all.slice(0, i).some((p) => p.specId === s.variantOf)
            ? `  ↳ ${s.name}`
            : s.name,
      })),
    [app.library.specs],
  );
  const ingredients = useMemo<readonly Named[]>(
    () =>
      app.library.ingredients
        .filter((i) => i.kind !== "dilution")
        .map((i) => ({ id: i.ingredientId, name: i.name }))
        .sort(byName),
    [app.library.ingredients],
  );
  // Names for everything, retired included, so an old mapping still reads.
  const specNames = useMemo(() => {
    const names = new Map(app.library.specs.map((s) => [s.specId, s.name]));
    // A variant reads with its primary, so "Rum Negroni" is plainly a Negroni.
    return new Map(
      app.library.specs.map((s) => {
        const primary = s.variantOf ? names.get(s.variantOf) : undefined;
        return [s.specId, primary ? `${s.name} (${primary} variant)` : s.name];
      }),
    );
  }, [app.library.specs]);
  const ingredientNames = useMemo(
    () => new Map(app.library.ingredients.map((i) => [i.ingredientId, i.name])),
    [app.library.ingredients],
  );

  const describe = (target: PosTarget | null): string => {
    if (!target) return "Not mapped";
    if (target.kind === "ignore") return "Ignored";
    if (target.kind === "spec") return `Recipe · ${specNames.get(target.specId) ?? target.specId}`;
    return `Pour · ${target.ml} mL ${ingredientNames.get(target.ingredientId) ?? target.ingredientId}`;
  };

  const sorted = useMemo(
    () => [...(items ?? [])].sort((a, b) => rank(a) - rank(b) || byName(a, b)),
    [items],
  );
  const unmapped = (items ?? []).filter((i) => !i.target).length;
  const guessed = (items ?? []).filter((i) => i.target && i.auto).length;

  return (
    <div className="org-section">
      <h3>
        Till items{items ? ` · ${items.length}` : ""}
        {items && unmapped > 0 ? ` · ${unmapped} unmapped` : ""}
      </h3>
      <div className="org-note">
        What each thing the till sells is. A recipe takes its whole build off the shelf; an
        ingredient pour takes one measure; ignore is for food, gift cards and the like. Unmapped
        items count towards takings and take nothing off stock.
        {guessed > 0
          ? ` ${guessed} ${guessed === 1 ? "was" : "were"} matched by name and should be checked.`
          : ""}{" "}
        A Square item with variations is one row per variation: give a drink's variants their own
        recipes in the library, then refresh, and anything not yet confirmed is matched again.
      </div>

      <div className="row int-actions">
        <button
          className="btn"
          disabled={busy !== null}
          onClick={() =>
            void run("refresh", async () => {
              const res = await integrationsApi.refreshItems(app.token);
              setItems(res.items ?? []);
              // The same call re-reads the merchant's locations and clears a
              // stale error, so the card above has to be read again too.
              await props.onRefreshed();
              const added = res.added ?? 0;
              const rematched = res.rematched ?? 0;
              const news =
                added > 0
                  ? `${added} new ${added === 1 ? "item" : "items"} from the Square catalogue.`
                  : "Nothing new in the Square catalogue.";
              return rematched > 0
                ? `${news} ${rematched} unconfirmed ${rematched === 1 ? "match was" : "matches were"} re-checked against the recipes and changed; the last four weeks are being recounted.`
                : news;
            })
          }
        >
          {busy === "refresh" ? "Reading the catalogue…" : "Refresh from Square"}
        </button>
      </div>

      {loadError && (
        <div className="msg err">
          {loadError}
          <div style={{ marginTop: 10 }}>
            <button className="btn sm" onClick={() => void load()}>
              Try again
            </button>
          </div>
        </div>
      )}
      {error && <div className="msg err">{error}</div>}
      {msg && <div className="msg ok">{msg}</div>}
      {!items && !loadError && <div className="label">Reading the till items…</div>}
      {items && items.length === 0 && (
        <div className="org-note">
          No till items yet. Refresh from Square to read the catalogue; anything sold that is not
          in it appears here after the next sync.
        </div>
      )}

      {sorted.length > 0 && (
        <div className="table">
          <div className="thead">
            <span className="m-name">Till item</span>
            <span className="pi-now">Mapped to</span>
            <span className="pi-edit" />
          </div>
          {sorted.map((item) => (
            <ItemRow
              // A saved row remounts, so its draft restarts from what was stored.
              key={`${item.key}|${item.updatedAt}|${item.auto}`}
              item={item}
              specs={specs}
              ingredients={ingredients}
              mappedTo={describe(item.target)}
              busy={busy === item.key}
              locked={busy !== null}
              onSave={(target) =>
                run(item.key, async () => {
                  const res = await integrationsApi.saveItem(app.token, item.key, target);
                  setItems((list) =>
                    (list ?? []).map((i) => (i.key === item.key ? res.item : i)),
                  );
                  return `${item.name || "Item"} saved. The last four weeks are being recounted.`;
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL: Readonly<Record<Draft["kind"], string>> = {
  "": "Not mapped",
  spec: "Recipe",
  ingredient: "Ingredient pour",
  ignore: "Ignore",
};

const KINDS: readonly Draft["kind"][] = ["", "spec", "ingredient", "ignore"];

function ItemRow(props: {
  readonly item: PosItem;
  readonly specs: readonly Named[];
  readonly ingredients: readonly Named[];
  readonly mappedTo: string;
  readonly busy: boolean;
  readonly locked: boolean;
  readonly onSave: (target: PosTarget | null) => Promise<boolean>;
}) {
  const { item } = props;
  const needsAttention = !item.target || item.auto;
  // Settled rows stay shut: a few hundred open selects is a slow page and a
  // long scroll past things nobody needs to touch.
  const [open, setOpen] = useState(needsAttention);
  const [draft, setDraft] = useState<Draft>(() => draftOf(item.target));

  const next = targetOf(draft);
  const unchanged = next !== undefined && sameTarget(next, item.target);
  // An `auto` match saved as it stands is somebody confirming it.
  const confirming = item.auto && unchanged && next !== null;
  const canSave = next !== undefined && (!unchanged || confirming);

  // A mapping to something no longer offered must still show in its select.
  const strayed = (list: readonly Named[], chosen: string) =>
    chosen !== "" && !list.some((x) => x.id === chosen);

  return (
    <div className="trow org-trow pi-row">
      <span className="m-name">
        <span className="n">{item.name || "Unnamed item"}</span>
        <span className="s">
          {item.variationName || "No variation"}
          {item.key.startsWith("name:") ? " · open price" : ""}
        </span>
      </span>

      <span className="pi-now">
        <span className={item.target ? "" : "warn"}>{props.mappedTo}</span>
        {item.target && item.auto && <span className="flag warn">Matched by name — check</span>}
      </span>

      <span className="pi-edit">
        {!open ? (
          <button className="btn sm" disabled={props.locked} onClick={() => setOpen(true)}>
            Change
          </button>
        ) : (
          <>
            <select
              aria-label={`What ${item.name} is`}
              value={draft.kind}
              disabled={props.locked}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as Draft["kind"] })}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>

            {draft.kind === "spec" && (
              <select
                aria-label={`Recipe for ${item.name}`}
                value={draft.specId}
                disabled={props.locked}
                onChange={(e) => setDraft({ ...draft, specId: e.target.value })}
              >
                <option value="">Choose a recipe…</option>
                {strayed(props.specs, draft.specId) && (
                  <option value={draft.specId}>{draft.specId} (not in the library)</option>
                )}
                {props.specs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}

            {draft.kind === "ingredient" && (
              <>
                <select
                  aria-label={`Ingredient for ${item.name}`}
                  value={draft.ingredientId}
                  disabled={props.locked}
                  onChange={(e) => setDraft({ ...draft, ingredientId: e.target.value })}
                >
                  <option value="">Choose an ingredient…</option>
                  {strayed(props.ingredients, draft.ingredientId) && (
                    <option value={draft.ingredientId}>
                      {draft.ingredientId} (not in the library)
                    </option>
                  )}
                  {props.ingredients.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
                <label className="pi-ml">
                  <input
                    className="num-input"
                    type="number"
                    min={0}
                    step={5}
                    inputMode="decimal"
                    aria-label={`Millilitres of it in one ${item.name}`}
                    value={draft.ml}
                    disabled={props.locked}
                    onChange={(e) => setDraft({ ...draft, ml: e.target.value })}
                  />
                  <span className="label">mL</span>
                </label>
              </>
            )}

            <button
              className="btn sm"
              disabled={props.locked || !canSave}
              onClick={() => next !== undefined && void props.onSave(next)}
            >
              {props.busy ? "Saving…" : confirming ? "Confirm" : "Save"}
            </button>
            {!needsAttention && (
              <button
                className="btn sm pi-cancel"
                disabled={props.locked}
                onClick={() => {
                  setDraft(draftOf(item.target));
                  setOpen(false);
                }}
              >
                Cancel
              </button>
            )}
          </>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------- Dext

/**
 * Dext has no API to post a document to, only an address to email one to —
 * so this card is an address and a switch, and there is no credential.
 *
 * An address off Dext's own domains is warned about and still saved: Dext has
 * changed domain once, and a bookkeeper may want slips at a mailbox that
 * forwards. `isDextAddress` is advisory here exactly as it is in the API.
 */
function Dext() {
  const app = useApp();
  const [state, setState] = useState<DextState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [address, setAddress] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const { busy, error, msg, run } = useRun();
  /**
   * The token is swapped for a fresh one late in a session, which re-runs the
   * load below. The fields are filled from the server only the first time, or
   * when a save or a removal asks for it — not out from under somebody typing.
   */
  const seeded = useRef(false);

  const load = useCallback(
    async (reseed = false) => {
      setLoadError(null);
      try {
        const res = await integrationsApi.dext(app.token);
        setState({
          dext: res.dext ?? null,
          sender: res.sender ?? "",
          senderReady: res.senderReady === true,
        });
        if (reseed || !seeded.current) {
          seeded.current = true;
          setAddress(res.dext?.emailAddress ?? "");
          setAutoSend(res.dext?.autoSend === true);
        }
      } catch (err) {
        setLoadError(message(err));
      }
    },
    [app.token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const dext = state?.dext ?? null;
  const typed = address.trim();
  const valid = isEmailAddress(typed);
  const dirty = typed !== (dext?.emailAddress ?? "") || autoSend !== (dext?.autoSend === true);

  return (
    <div className="org-section">
      <h3>Dext · receipts</h3>
      <div className="org-note">
        When an expense has a slip, BarOS emails the original file to your Dext account, where it
        lands in the Costs inbox for the bookkeeper.
      </div>

      {loadError && (
        <div className="msg err">
          {loadError}
          <div style={{ marginTop: 10 }}>
            <button className="btn sm" onClick={() => void load()}>
              Try again
            </button>
          </div>
        </div>
      )}
      {!state && !loadError && <div className="label">Reading the Dext settings…</div>}
      {error && <div className="msg err">{error}</div>}
      {msg && <div className="msg ok">{msg}</div>}

      {state && (
        <div className="org-card">
          {!state.senderReady && (
            <div className="msg int-warn">
              Email cannot leave this environment yet, so nothing will reach Dext. The address
              can still be saved; slips will start going once sending is switched on.
            </div>
          )}

          <label className="field int-address">
            <span>Extract by Email address</span>
            <input
              type="email"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={address}
              placeholder="yourbusiness@dext.cc"
              disabled={busy !== null}
              onChange={(e) => setAddress(e.target.value)}
            />
          </label>
          <div className="org-note">
            In Dext it is under Business settings › Extraction › Extract by email, or Add documents
            › Email. Use the <strong>Costs</strong> address, not the Sales one.
          </div>
          {typed !== "" && !valid && (
            <div className="org-note int-bad">That does not read as a single email address.</div>
          )}
          {valid && !isDextAddress(typed) && (
            <div className="org-note int-warn">
              That is not one of Dext's own addresses. It can still be saved — a mailbox that
              forwards to Dext works — but check the first slip arrives.
            </div>
          )}

          <div className="row int-option">
            <div className="org-note">
              <strong>Send every slip automatically.</strong> Off, a slip goes when somebody
              presses Send to Dext on the expense.
            </div>
            <OnOff
              label="Send every slip automatically"
              value={autoSend}
              disabled={busy !== null}
              onChange={setAutoSend}
            />
          </div>

          {state.sender && (
            <div className="org-note">
              Slips are sent from <span className="mono">{state.sender}</span>. Please do not block
              or filter that address in Dext, or they will not arrive.
            </div>
          )}

          <div className="row int-actions">
            {dext && (
              <button
                className="btn danger"
                disabled={busy !== null}
                onClick={() =>
                  void run("remove", async () => {
                    const ok = window.confirm(
                      "Stop sending slips to Dext? Anything already sent stays in Dext.",
                    );
                    if (!ok) return null;
                    await integrationsApi.removeDext(app.token);
                    setState((s) => (s ? { ...s, dext: null } : s));
                    setAddress("");
                    setAutoSend(false);
                    return "Dext removed. No more slips will be sent.";
                  })
                }
              >
                {busy === "remove" ? "Removing…" : "Remove"}
              </button>
            )}
            <span className="spacer" />
            <button
              className="btn primary"
              disabled={busy !== null || !valid || !dirty}
              onClick={() =>
                void run("save", async () => {
                  const res = await integrationsApi.saveDext(app.token, typed, autoSend);
                  setState((s) => (s ? { ...s, dext: res.dext } : s));
                  setAddress(res.dext.emailAddress);
                  setAutoSend(res.dext.autoSend === true);
                  return res.recognised === false
                    ? "Saved. That is not one of Dext's own addresses, so check the first slip arrives."
                    : "Dext address saved.";
                })
              }
            >
              {busy === "save" ? "Saving…" : "Save"}
            </button>
          </div>
          {dext && (
            <div className="org-foot-note">Last changed {when(dext.updatedAt)}</div>
          )}
        </div>
      )}
    </div>
  );
}
